
import Vue from 'vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { ApolloError } from '@apollo/client/errors';
import { DESCRIPTION } from '@/constants/defaultMeta';
import { PUBLIC_PORTFOLIO_ANNOUNCEMENT_TOOLTIP_NAME } from '@/components/common/AnnouncementModal/constants';
import { PP_NEW_MENU_TOOLTIP } from '@/components/common/BravadoRightNavigation/constants';
import PortfolioCurrentUserQuery from '@/apollo/queries/Portfolio/PortfolioCurrentUser.query';
import PortfolioSpinner from '@/components/partials/public-portfolio/PortfolioSpinner.vue';
import PublicPortfolioIndexPageCurrentJobsInquiryQuery from '@/components/partials/public-portfolio/PublicPortfolioIndexPageCurrentJobsInquiry.query';
import { getPortfolioPath } from '@/utils/routes';
import PortfolioEdit from '@/components/partials/portfolio/PortfolioEdit/PortfolioEdit.vue';
import { testimonialStates } from '@/components/partials/public-portfolio/PublicPortfolioTestimonials/constants';
import type {
  PortfolioCurrentUser,
  PublicPortfolioCurrentUser,
  PublicPortfolioUser,
  CurrentUser,
  JobsInquiry,
  Testimonial,
  PostConnection,
} from '~/types/graphql';
import PortfolioCurrentUserLinkedInImport from '~/apollo/queries/PortfolioCurrentUserLinkedInImport.query';
import { PublicPortfolioCurrentUserQuery } from '~/apollo/queries/PublicPortfolio/PublicPortfolioCurrentUser.query';
import PublicPortfolioUserQuery from '~/apollo/queries/PublicPortfolio/PublicPortfolioUser.query';
import PublicPortfolioUserTestifySignQuery from '~/apollo/queries/PublicPortfolio/PublicPortfolioUserTestifySign.query';
import PublicPortfolioPostsQuery from '~/apollo/queries/PublicPortfolio/PublicPortfolioPosts.query';
import RealUserQuery from '~/apollo/queries/realUser.gql';
import PublicPortfolioPreview from '~/components/partials/public-portfolio/PublicPortfolioPreview.vue';
import { testimonialsByStates } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/utils';

const NEW_MENU_TOOLTIP = 'public_portfolio_new_menu_tooltip';

const PROFILE_IS_HIDDEN_ERROR_MESSAGE =
  'GraphQL error: An object of type PublicPortfolioUser was hidden due to permissions';

interface IData {
  publicPortfolioCurrentUser: PublicPortfolioCurrentUser | null;
  publicPortfolioUser: PublicPortfolioUser | null;
  publicPortfolioUserTestifySign: string | null;
  currentUser: CurrentUser | null;
  currentJobsInquiry: JobsInquiry | null;
  userPublicPosts: PostConnection | null;
}

export default Vue.extend({
  name: 'PublicPortfolioIndexPage',
  components: {
    PortfolioSpinner,
    PortfolioEdit,
    PublicPortfolioPreview,
  },
  apollo: {
    linkedInImportStatus: {
      query: PortfolioCurrentUserLinkedInImport,
      pollInterval: 1000,
      skip(): boolean {
        return !this.$auth.loggedIn || this.linkedInImportState !== 'pending';
      },
      result({ data }: { data: { portfolioCurrentUser: PortfolioCurrentUser } }): void {
        if (data.portfolioCurrentUser.lastLinkedinSyncedAt) {
          this.updateLinkedInImportState('idle');

          if (data?.portfolioCurrentUser?.lastLinkedinSyncError) {
            this.$router.push(`?dynamic_modal=${this.$constants.dynamicModals.PUBLIC_PORTFOLIO_LINKEDIN_ERROR_MODAL}`);
          } else {
            this.fetchProfile({ slug: this.$route.params.slug });
          }
        }
      },
      fetchPolicy: 'no-cache',
    },
    publicPortfolioCurrentUser: {
      query: PublicPortfolioCurrentUserQuery,
      skip(): boolean {
        return !this.isPortfolioForCurrentUser;
      },
    },
    publicPortfolioUser: {
      query: PublicPortfolioUserQuery,
      variables(): Record<string, string> {
        return {
          slug: this.$route.params.slug,
        };
      },
      skip(): boolean {
        return this.isPortfolioForCurrentUser;
      },
    },
    publicPortfolioUserTestifySign: {
      query: PublicPortfolioUserTestifySignQuery,
      variables(): Record<string, string> {
        return {
          slug: this.$route.params.slug,
        };
      },
      skip(): boolean {
        return !this.isPortfolioForCurrentUser;
      },
    },
    currentUser: {
      query: PortfolioCurrentUserQuery,
      skip(): boolean {
        return !this.isPortfolioForCurrentUser;
      },
    },
    currentJobsInquiry: {
      query: PublicPortfolioIndexPageCurrentJobsInquiryQuery,
      error(e): void {
        this.$notice.error(e);
      },
      skip(): boolean {
        return !this.isPortfolioForCurrentUser;
      },
    },
    userPublicPosts: {
      query: PublicPortfolioPostsQuery,
      fetchPolicy: 'network-only',
      variables(): Record<string, any> {
        return {
          slug: this.$route.params.slug,
          first: 5,
        };
      },
    },
  },
  async middleware({ app, params, redirect, $utils, route, error }) {
    // using this for edgecases on client side
    if (process.client && route.path.startsWith('/portfolio')) {
      $utils.routeReplaceWithQuery({ path: getPortfolioPath(route.params.slug) });
      return;
    }

    try {
      const result = await app.apolloProvider?.defaultClient.query({
        query: RealUserQuery,
        variables: {
          slug: params.slug,
        },
      });

      if (!result?.data?.publicPortfolioUser) {
        error({ statusCode: 404, message: 'Page Not Found' });
      }

      if (
        result?.data?.publicPortfolioUser?.realUser?.slug &&
        result?.data?.publicPortfolioUser?.realUser?.slug !== params.slug
      ) {
        redirect(302, getPortfolioPath(result.data.publicPortfolioUser.realUser.slug));
      }
    } catch (e) {
      app.$sentryUtils.error(e);

      if (e instanceof ApolloError && e?.message === PROFILE_IS_HIDDEN_ERROR_MESSAGE) {
        error({ statusCode: 404, message: 'Page Not Found' });
      }
    }
  },
  async asyncData({ store, app, params }): Promise<any> {
    try {
      await store.dispatch('portfolio/load', params);
    } catch (e) {
      app.$utils.nuxtErrorPage(e);
      app.$sentryUtils.error(e);
    }
  },
  data(): IData {
    return {
      publicPortfolioUser: null,
      publicPortfolioCurrentUser: null,
      publicPortfolioUserTestifySign: '',
      currentUser: null,
      currentJobsInquiry: null,
      userPublicPosts: null,
    };
  },
  head(): object {
    const {
      auto_name: fullName,
      position,
      current_company: company,
      avatar_url: image,
      public_url: url,
      description: userDescription,
      is_discoverable: isDiscoverable,
    } = this.user;

    const headline = [position, company].filter(Boolean).join(' at ');
    const nameAndTitle = [fullName, headline].filter(Boolean).join(', ');
    const title = [nameAndTitle, 'Bravado Portfolio'].filter(Boolean).join(' | ');
    let description = `${fullName} is a Bravado member. ${DESCRIPTION}`;

    if (this.latestVisibleTestimonial?.description && this.latestVisibleTestimonial.description.length > 150) {
      description = this.latestVisibleTestimonial.description;
    } else if (userDescription && userDescription.length > 100) {
      description = userDescription;
    }

    return this.$seo({
      title,
      description,
      // TODO: figure out why in test C123 url is undefined for logged in user portfolio
      canonical: url || '',
      og: {
        url,
        ...(image && {
          image: {
            url: image,
          },
        }),
      },
      ...(!isDiscoverable && {
        robots: 'noindex',
      }),
    });
  },
  computed: {
    ...mapState('portfolio', ['user', 'linkedInImportState']),
    isPortfolioForCurrentUser(): boolean {
      return this.$auth.loggedIn && this.$auth.user.slug === this.$route.params.slug;
    },
    latestVisibleTestimonial(): Testimonial | undefined {
      const testimonials = this.isPortfolioForCurrentUser
        ? this.publicPortfolioCurrentUser?.testimonials
        : this.publicPortfolioUser?.testimonials;

      if (!testimonials) return undefined;

      const [testimonial] = testimonialsByStates(testimonials, testimonialStates.VISIBLE_TESTIMONIAL_STATES);

      return testimonial;
    },
  },
  watch: {
    '$auth.loggedIn'(newVal: boolean): void {
      if (newVal && this.$auth.user?.slug === this.$route.params.slug) {
        this.fetchProfile({ slug: this.$route.params.slug });
      }
    },
  },
  mounted(): void {
    const isNew = this.$auth.loggedIn && !this.$tooltip.isPresent(NEW_MENU_TOOLTIP);

    if (isNew) {
      this.$tooltip.close(NEW_MENU_TOOLTIP);
    }
  },
  beforeMount(): void {
    [PP_NEW_MENU_TOOLTIP, PUBLIC_PORTFOLIO_ANNOUNCEMENT_TOOLTIP_NAME].forEach(tooltip => {
      const isNew = !!(this.$auth.loggedIn && !this.$tooltip.isPresent(tooltip));

      if (isNew) {
        this.$tooltip.close(tooltip);
      }
    });
  },
  methods: {
    ...mapMutations('portfolio', ['updateLinkedInImportState']),
    ...mapActions('portfolio', { fetchProfile: 'load' }),
  },
});
